import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import NewsCard from "@/components/base/NewsCard";
import { movies } from "@/mocks/movies";
import { newsArticles } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type Tab = "movie" | "news";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(initial);
  const [tab, setTab] = useState<Tab>("movie");
  const [visible, setVisible] = useState(8);

  useDocumentMeta(
    initial ? `“${initial}”的搜索结果 - 芒果tv` : "搜索 - 芒果tv",
    "在芒果tv 搜索你感兴趣的电视剧、综艺、电影、动漫、纪录片与娱乐资讯。"
  );

  useEffect(() => {
    setKeyword(initial);
    setVisible(8);
  }, [initial]);

  const q = keyword.trim().toLowerCase();

  const movieResults = useMemo(() => {
    if (!q) return [];
    return movies.filter((m) =>
      [m.title, m.channel, m.region, m.director, m.synopsis, ...m.genres, ...m.cast, ...m.tags]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [q]);

  const newsResults = useMemo(() => {
    if (!q) return [];
    return newsArticles.filter((a) =>
      [a.title, a.summary, a.category, ...a.content, ...a.tags]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [q]);

  const submit = (value: string) => {
    setKeyword(value);
    if (value.trim()) setParams({ q: value.trim() });
    else setParams({});
    setVisible(8);
  };

  const reset = () => {
    setKeyword("");
    setParams({});
    setVisible(8);
  };

  const list = tab === "movie" ? movieResults : newsResults;
  const hasQuery = q.length > 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950">搜索中心</h1>
        <p className="mt-1 text-sm text-foreground-500">
          输入关键词搜索影视作品与娱乐资讯（前端演示，非实时在线搜索）。
        </p>

        <div className="mt-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center rounded-full border border-background-300 bg-background-100 pl-4 pr-1">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit(keyword)}
              placeholder="搜索剧名、类型、演员或资讯关键词"
              className="w-full bg-transparent px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                onClick={reset}
                aria-label="清空"
                className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => submit(keyword)}
            className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <div className="flex rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => {
                setTab("movie");
                setVisible(8);
              }}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                tab === "movie" ? "bg-primary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              影视（{movieResults.length}）
            </button>
            <button
              type="button"
              onClick={() => {
                setTab("news");
                setVisible(8);
              }}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                tab === "news" ? "bg-primary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              资讯（{newsResults.length}）
            </button>
          </div>
        </div>
      </div>

      {!hasQuery ? (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
            输入关键词开始搜索
          </p>
          <p className="mt-1 text-sm text-foreground-500">例如：悬疑、都市、纪录片、综艺。</p>
        </div>
      ) : list.length > 0 ? (
        <>
          <p className="mt-5 text-sm text-foreground-500">
            关键词 <strong className="text-primary-600">“{keyword.trim()}”</strong> 共找到{" "}
            <strong className="text-primary-600">{list.length}</strong> 条
            {tab === "movie" ? "影视结果" : "资讯结果"}
          </p>

          {tab === "movie" ? (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {movieResults.slice(0, visible).map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {newsResults.slice(0, visible).map((a) => (
                <NewsCard key={a.id} article={a} />
              ))}
            </div>
          )}

          {visible < list.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + 8)}
                className="rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                加载更多
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
            没有找到相关内容
          </p>
          <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或重置搜索。</p>
          <button
            type="button"
            onClick={reset}
            className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            重置搜索
          </button>
        </div>
      )}
    </div>
  );
}